import React, { useState, useEffect } from 'react';
import api from '../auth/api';
import PhonemePicker from './PhonemePicker';
import ImageUpload from './ImageUpload';

/**
 * ExerciseForm Component
 * 
 * Form for creating and editing exercises with syllable-based phoneme entry.
 * This is the core component for the dynamic exercise system.
 * 
 * Props:
 * - exercise: object - Exercise to edit (null for new exercise)
 * - onSave: () => void - Callback after successful save
 * - onCancel: () => void - Callback when form is cancelled
 */
const ExerciseForm = ({ exercise = null, onSave, onCancel }) => {
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Form state
    const [formData, setFormData] = useState({
        word_text: '',
        language: 'hi-IN',
        category_id: '',
        sub_category_id: '',
        image_url: ''
    });

    // Syllables state - array of syllables, each with phonemes
    const [syllables, setSyllables] = useState([
        {
            text: '',
            phonemes: [
                { ipa_symbol: '', position: 'initial', sequence_order: 1 }
            ]
        }
    ]);

    useEffect(() => {
        fetchCategories();

        // If editing, populate form
        if (exercise) {
            setFormData({
                word_text: exercise.word_text,
                language: exercise.language,
                category_id: exercise.category_id,
                sub_category_id: exercise.sub_category_id || '',
                image_url: exercise.image_url || ''
            });

            // Reconstruct syllables from target_phonemes
            if (exercise.target_phonemes && exercise.target_phonemes.length > 0) {
                reconstructSyllables(exercise.target_phonemes);
            }
        }
    }, [exercise]);

    useEffect(() => {
        if (formData.category_id) {
            fetchSubCategories(formData.category_id);
        } else {
            setSubCategories([]);
        }
    }, [formData.category_id]);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError('Failed to load categories');
        }
    };

    const fetchSubCategories = async (categoryId) => {
        try {
            const category = categories.find(c => c.id === parseInt(categoryId));
            setSubCategories(category?.sub_categories || []);
        } catch (err) {
            console.error('Error fetching subcategories:', err);
        }
    };

    const reconstructSyllables = (targetPhonemes) => {
        // Group phonemes by syllable_index
        const syllableGroups = {};
        targetPhonemes.forEach(phoneme => {
            const syllableIdx = phoneme.syllable_index || 0;
            if (!syllableGroups[syllableIdx]) {
                syllableGroups[syllableIdx] = [];
            }
            syllableGroups[syllableIdx].push(phoneme);
        });

        // Convert to syllables array
        const reconstructed = Object.keys(syllableGroups).map(idx => ({
            text: '', // We don't store syllable text, so leave empty
            phonemes: syllableGroups[idx].sort((a, b) => a.sequence_order - b.sequence_order)
        }));

        setSyllables(reconstructed.length > 0 ? reconstructed : [
            { text: '', phonemes: [{ ipa_symbol: '', position: 'initial', sequence_order: 1 }] }
        ]);
    };

    const handleAddSyllable = () => {
        setSyllables([
            ...syllables,
            {
                text: '',
                phonemes: [
                    { ipa_symbol: '', position: 'initial', sequence_order: 1 }
                ]
            }
        ]);
    };

    const handleRemoveSyllable = (syllableIndex) => {
        if (syllables.length === 1) {
            alert('At least one syllable is required');
            return;
        }
        setSyllables(syllables.filter((_, idx) => idx !== syllableIndex));
    };

    const handleSyllableTextChange = (syllableIndex, text) => {
        const updated = [...syllables];
        updated[syllableIndex].text = text;
        setSyllables(updated);
    };

    const handleAddPhoneme = (syllableIndex) => {
        const updated = [...syllables];
        const currentPhonemes = updated[syllableIndex].phonemes;
        const nextOrder = currentPhonemes.length + 1;

        updated[syllableIndex].phonemes.push({
            ipa_symbol: '',
            position: 'medial',
            sequence_order: nextOrder
        });
        setSyllables(updated);
    };

    const handleRemovePhoneme = (syllableIndex, phonemeIndex) => {
        const updated = [...syllables];
        if (updated[syllableIndex].phonemes.length === 1) {
            alert('At least one phoneme per syllable is required');
            return;
        }
        updated[syllableIndex].phonemes.splice(phonemeIndex, 1);

        // Reorder sequence
        updated[syllableIndex].phonemes.forEach((p, idx) => {
            p.sequence_order = idx + 1;
        });

        setSyllables(updated);
    };

    const handlePhonemeChange = (syllableIndex, phonemeIndex, field, value) => {
        const updated = [...syllables];
        updated[syllableIndex].phonemes[phonemeIndex][field] = value;
        setSyllables(updated);
    };

    const validateForm = () => {
        if (!formData.word_text.trim()) {
            setError('Word text is required');
            return false;
        }
        if (!formData.category_id) {
            setError('Category is required');
            return false;
        }

        // Validate syllables
        for (let i = 0; i < syllables.length; i++) {
            const syllable = syllables[i];
            if (syllable.phonemes.length === 0) {
                setError(`Syllable ${i + 1} must have at least one phoneme`);
                return false;
            }
            for (let j = 0; j < syllable.phonemes.length; j++) {
                if (!syllable.phonemes[j].ipa_symbol) {
                    setError(`Syllable ${i + 1}, Phoneme ${j + 1}: Please select a phoneme`);
                    return false;
                }
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Flatten syllables into target_phonemes array
            let globalSequence = 1;
            const target_phonemes = [];

            syllables.forEach((syllable, syllableIdx) => {
                syllable.phonemes.forEach(phoneme => {
                    target_phonemes.push({
                        ipa_symbol: phoneme.ipa_symbol,
                        position: phoneme.position,
                        sequence_order: globalSequence++,
                        syllable_index: syllableIdx + 1
                    });
                });
            });

            const payload = {
                ...formData,
                category_id: parseInt(formData.category_id),
                sub_category_id: formData.sub_category_id ? parseInt(formData.sub_category_id) : null,
                target_phonemes
            };

            if (exercise) {
                // Update existing exercise
                await api.put(`/exercises/${exercise.id}`, payload);
            } else {
                // Create new exercise
                await api.post('/exercises', payload);
            }

            onSave();
        } catch (err) {
            console.error('Error saving exercise:', err);
            setError(err.response?.data?.detail || 'Failed to save exercise');
        } finally {
            setLoading(false);
        }
    };

    // Filter categories by language
    const filteredCategories = categories.filter(cat =>
        cat.language === formData.language || cat.language === 'both'
    );

    return (
        <div className="exercise-form">
            <h2>{exercise ? 'Edit Exercise' : 'Create New Exercise'}</h2>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div className="form-section">
                    <h3>Basic Information</h3>

                    <div className="form-group">
                        <label>Word Text *</label>
                        <input
                            type="text"
                            value={formData.word_text}
                            onChange={(e) => setFormData({ ...formData, word_text: e.target.value })}
                            required
                            maxLength={100}
                            placeholder="e.g., पानी"
                        />
                    </div>

                    <div className="form-group">
                        <label>Language *</label>
                        <select
                            value={formData.language}
                            onChange={(e) => setFormData({ ...formData, language: e.target.value, category_id: '' })}
                            required
                        >
                            <option value="hi-IN">Hindi</option>
                            <option value="en-US">English</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Category *</label>
                        <select
                            value={formData.category_id}
                            onChange={(e) => setFormData({ ...formData, category_id: e.target.value, sub_category_id: '' })}
                            required
                        >
                            <option value="">Select category...</option>
                            {filteredCategories.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {subCategories.length > 0 && (
                        <div className="form-group">
                            <label>SubCategory</label>
                            <select
                                value={formData.sub_category_id}
                                onChange={(e) => setFormData({ ...formData, sub_category_id: e.target.value })}
                            >
                                <option value="">None</option>
                                {subCategories.map(subCat => (
                                    <option key={subCat.id} value={subCat.id}>
                                        {subCat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Image</label>
                        <ImageUpload
                            value={formData.image_url}
                            onChange={(url) => setFormData({ ...formData, image_url: url })}
                        />
                    </div>
                </div>

                {/* Syllable-Based Phoneme Entry */}
                <div className="form-section">
                    <div className="section-header">
                        <h3>Syllables & Phonemes *</h3>
                        <button
                            type="button"
                            onClick={handleAddSyllable}
                            className="btn btn-sm btn-secondary"
                        >
                            + Add Syllable
                        </button>
                    </div>

                    {syllables.map((syllable, syllableIdx) => (
                        <div key={syllableIdx} className="syllable-block">
                            <div className="syllable-header">
                                <h4>Syllable {syllableIdx + 1}</h4>
                                {syllables.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSyllable(syllableIdx)}
                                        className="btn btn-sm btn-danger"
                                    >
                                        Remove Syllable
                                    </button>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Syllable Text (optional)</label>
                                <input
                                    type="text"
                                    value={syllable.text}
                                    onChange={(e) => handleSyllableTextChange(syllableIdx, e.target.value)}
                                    placeholder={`e.g., ${formData.language === 'hi-IN' ? 'पा' : 'wa'}`}
                                    maxLength={20}
                                />
                            </div>

                            <div className="phonemes-list">
                                <label>Phonemes:</label>
                                {syllable.phonemes.map((phoneme, phonemeIdx) => (
                                    <div key={phonemeIdx} className="phoneme-row">
                                        <span className="phoneme-number">{phonemeIdx + 1}.</span>
                                        <PhonemePicker
                                            language={formData.language}
                                            value={phoneme.ipa_symbol}
                                            onChange={(ipa) => handlePhonemeChange(syllableIdx, phonemeIdx, 'ipa_symbol', ipa)}
                                            position={phoneme.position}
                                            onPositionChange={(pos) => handlePhonemeChange(syllableIdx, phonemeIdx, 'position', pos)}
                                        />
                                        {syllable.phonemes.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemovePhoneme(syllableIdx, phonemeIdx)}
                                                className="btn btn-sm btn-danger"
                                                title="Remove phoneme"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => handleAddPhoneme(syllableIdx)}
                                    className="btn btn-sm btn-secondary"
                                >
                                    + Add Phoneme
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Form Actions */}
                <div className="form-actions">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn btn-secondary"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : (exercise ? 'Update Exercise' : 'Create Exercise')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ExerciseForm;
